import { LitElement, html, nothing } from 'lit';
import { trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../../../shared/services/debug.service.js';
import { ApplicationService } from '../../../../../services/application.service.js';
import { REFI_PERSONAL_CONST } from '../../../../../shared/constants/app.constants.js';
import '@cof/fs-checkbox-wc/fs-checkbox.js';
import '@cof/fs-select-wc/fs-select.js';
import { globalStyle } from '../../../../../global-styles.style.js';
import { style } from './refi-text-opt-in.style.js';
import './refi-text-opt-in-modal.component.js';
import '@cof/fs-phone-number-field-wc/fs-phone-number-field.js';
import { stratum } from '../../../../../../constants/stratum.js';

class RefiPersonalTextOptInComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepCommunication');
    this.applicant = {};
    this.validPhoneType = false;
    this.showOptOutWarning = false;
    this.openOptInModal = false;
    this.values = {};
  }

  static properties = {
    applicant: { type: Object, reflect: true },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    prefix: { type: String, attribute: true },
    validPhoneType: { type: Boolean, reflect: true },
    phoneType: { type: String, reflect: true },
    phoneNumber: { type: String, attribute: 'phone-number', reflect: true },
    smsOptIn: { type: Boolean, reflect: true },
    openOptInModal: { type: Boolean, reflect: true },
    showOptOutWarning: { type: Boolean, reflect: true },
    isMobileNumber: { type: Boolean, reflect: true },
  };

  static styles = [globalStyle, style];

  connectedCallback() {
    super.connectedCallback();
    this.application = ApplicationService.Application;
    this.applicant = this.isPrimary
      ? this.application.primaryApplicant
      : this.application.coApplicant;
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    this.phoneId = `${this.coAppPrefix}phone`;
    this.phoneTypeId = `${this.coAppPrefix}phoneType`;
    this.smsOptIn = this.applicant.smsOptIn;
    this.values = {
      phoneType: this.applicant.phoneType ?? null,
      phoneNumber: this.phoneNumber,
      smsOptIn: this.smsOptIn,
    };
    this.isMobileNumber = this.values.phoneType === 'mobile';
    trackAnalyticsEvent('saw tcpa experience');
    if (this.isMobileNumber) {
      trackAnalyticsEvent(
        this.values.smsOptIn ? 'mobilefound:tcpa yes' : 'mobilefound:tcpa no'
      );
    } else {
      trackAnalyticsEvent('mobile not found');
    }
  }

  render() {
    return html`
      <div class="personal__text-opt-in">
        <fs-select
          id="${this.phoneTypeId}"
          name="phone-type-select"
          @change="${(ev) => {
            this.changed('phoneType', ev);
            this.validPhoneType =
              ev.target.value !== null && ev.target.value !== '';
            if (this.validPhoneType) {
              this.values.phoneType = ev.target.value;
              this.isMobileNumber = this.values.phoneType === 'mobile';
              if (this.values.phoneType !== 'mobile') {
                this.smsOptIn = false;
                this.values.smsOptIn = false;
              }
              stratum.publishTag(
                TagKey.PERSONAL_COMMUNICATION_PHONE_NUMBER_TYPE,
                { replacements: { value: this.values.phoneType } }
              );
              trackAnalyticsEvent('change mobile type');
            }
          }}"
          @blur="${(ev) => {
            if (ev.target.validity.valid) {
              stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                replacements: { value: 'phoneType' },
              });
            } else {
              stratum.publishTag(
                TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                {
                  replacements: {
                    value:
                      'Please select a phone number type from the dropdown menu.',
                  },
                }
              );
            }
          }}"
          value="${this.values.phoneType}"
          required
        >
          <span slot="label">Phone type</span>
          <option value="">Select a phone type</option>
          ${REFI_PERSONAL_CONST.PHONE_TYPES.map(
            (phoneType) => html` <option
              ?selected="${this.values.phoneType === phoneType.toLowerCase()}"
              value="${phoneType.toLowerCase()}"
            >
              ${phoneType}
            </option>`
          )}
        </fs-select>
        <fs-body-text
          size="small"
          variant="feedback-negative"
          ?hidden="${this.validPhoneType}"
          id="${`${this.phoneTypeId}-validPhoneType`}"
        >
          Please select a phone number type from the dropdown menu.
        </fs-body-text>
      </div>

      <div class="personal__phone">
        <fs-phone-number-field
          id="${this.phoneId}"
          autocomplete="tel"
          name="phone"
          type="tel"
          value-missing-message="Please enter a valid phone number"
          maxLength="14"
          pattern="^([2-9]\\d{2})-?[2-9]\\d{2}-?\\d{4}$"
          required
          @change="${(ev) => {
            if (ev.target.value.startsWith('1-')) {
              ev.target.value = ev.target.value.slice(2);
            }
            this.changed('phoneNumber', ev);
            trackAnalyticsEvent('change mobile num');
            this.openModal();
            this.phoneChanged = true;
          }}"
          @blur="${(ev) => {
            this.changed('phoneNumber', ev);
            if (this.phoneChanged) {
              this.openModal();
              this.phoneChanged = false;
            }
            if (ev.target.validity.valid) {
              stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                replacements: { value: 'phoneNumber' },
              });
            } else {
              stratum.publishTag(
                TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                {
                  replacements: {
                    value:
                      'Please enter a valid phone number XXX-XXX-XXXX or XXXXXXXXXX',
                  },
                }
              );
            }
          }}"
          value="${this.values.phoneNumber}"
          pattern-mismatch-message="Please enter a valid phone number XXX-XXX-XXXX or XXXXXXXXXX"
        >
          Phone number
        </fs-phone-number-field>
      </div>
      <fs-body-text
        ?hidden="${!this.isMobileNumber}"
        size="small"
        class="callout"
      >
        Changes to this number will apply to all your Capital One accounts.
      </fs-body-text>

      <div ?hidden="${!this.isMobileNumber}">
        <fs-checkbox
          id="phoneConsentCheckbox"
          name="smsOptIn"
          value="${this.smsOptIn}"
          .checked="${this.smsOptIn}"
          @change="${() => {
            this.values.smsOptIn = !this.values.smsOptIn;
            stratum.publishTag(TagKey.PERSONAL_COMMUNICATION_SMS_OPT_ON_PAGE, {
              replacements: { value: this.smsOptIn.toString() },
            });
            this.openModal();
          }}"
        >
          I want to receive text messages about my application (optional)
        </fs-checkbox>

        <fs-alert id="smsOptInAlert" type="warning" ?hidden="${!this.smsOptIn}">
          You'll receive some updates about your application via text messages,
          but continue to check your email for important documents and other
          information.
        </fs-alert>

        <fs-alert class="smsOptInError" ?hidden="${!this.showOptOutWarning}">
          By not updating your phone consent, you'll be opting out of automated
          alerts for this phone number across all your Capital One accounts.
          <a
            class="fs-link fs-link--primary"
            href="#"
            role="button"
            @click="${(ev) => {
              ev.preventDefault();
              this.openModal();
            }}"
          >
            Update consent.
          </a>
        </fs-alert>
      </div>

      ${this.openOptInModal
        ? html`<refi-text-opt-in-modal
            @modal-closed="${(ev) => {
              this.closeModal(ev);
            }}"
            ?open="${this.openOptInModal}"
            is-primary="${this.isPrimary}"
            phone-number="${this.values.phoneNumber}"
            phone-type="${this.values.phoneType}"
          ></refi-text-opt-in-modal>`
        : nothing}
    `;
  }

  firstUpdated() {
    const phoneField = this.shadowRoot.getElementById(this.phoneId);
    this.dispatchEvent(
      new CustomEvent('validity', {
        bubbles: true,
        composed: true,
        detail: {
          name: 'phoneNumber',
          isValid: phoneField.validity.valid && phoneField?.value !== '',
        },
      })
    );
    const phoneTypeField = this.shadowRoot.getElementById(this.phoneTypeId);
    this.validPhoneType =
      phoneTypeField?.validity.valid &&
      phoneTypeField?.value !== null &&
      phoneTypeField?.value !== '';
    this.dispatchEvent(
      new CustomEvent('validity', {
        bubbles: true,
        composed: true,
        detail: {
          name: 'phoneType',
          isValid: this.validPhoneType,
        },
      })
    );
    stratum.publishTag(TagKey.VIEW_PERSONAL_CONTACT);
    trackAnalyticsEvent(TagKey.VIEW_PERSONAL_CONTACT);
  }

  openModal() {
    if (this.isMobileNumber) {
      this.smsOptIn = false;
      const phoneField = this.shadowRoot.getElementById(this.phoneId);
      if (phoneField.validity.valid && phoneField.value !== '') {
        this.openOptInModal = true;
      }
    }
  }

  closeModal(ev) {
    this.smsOptIn = ev.detail.data.smsOptIn;
    this.values.smsOptIn = this.smsOptIn;
    this.values.phoneNumber = ev.detail.data.phoneNumber;
    this.shadowRoot.getElementById('phoneConsentCheckbox').checked =
      this.smsOptIn;
    this.openOptInModal = false;
    this.showOptOutWarning = !this.smsOptIn;
    this.requestUpdate();
  }

  changed(name, ev) {
    if (ev.target.validity.valid) {
      this.values[name] = ev.target.value.trim();
    }
    this.dispatchEvent(
      new CustomEvent('validity', {
        bubbles: true,
        composed: true,
        detail: {
          name,
          isValid:
            ev.target?.required &&
            ev.target.validity.valid &&
            ev.target?.value !== '',
        },
      })
    );
  }
}

customElements.define('refi-text-opt-in', RefiPersonalTextOptInComponent);
